/**
 * Which theme is in force, and how it is remembered (T-019).
 *
 * Three states, not two. "system" is a real choice and the default — it means
 * follow the operating system, so a shop PC set to switch at dusk carries the
 * app with it. The other two are explicit overrides that must beat the OS in
 * BOTH directions: someone on a dark-mode laptop who picks light has to get
 * light, which is why tokens.css scopes its media query to :not([data-theme])
 * rather than relying on source order.
 */

export const THEMES = ['system', 'light', 'dark']

const STORAGE_KEY = 'theme'

/**
 * localStorage, not the shop-scoped store.
 *
 * Theme is a property of the screen someone is sitting at, not of their
 * account: the counter PC by the window wants light, the back-office one at
 * night wants dark, and both are used by whoever is on shift. Keying it per
 * user would fight that. It is also the one preference that must be readable
 * before anyone logs in.
 */
export function getStoredTheme() {
    try {
        const stored = localStorage.getItem(STORAGE_KEY)
        return THEMES.includes(stored) ? stored : 'system'
    } catch (e) {
        // Private browsing can refuse to read. Following the OS is the right
        // thing to fall back to.
        return 'system'
    }
}

export function systemPrefersDark() {
    // matchMedia is missing in jsdom unless the test provides it, and in a few
    // older embedded browsers.
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false
    return window.matchMedia('(prefers-color-scheme: dark)').matches
}

/** What the user will actually see: 'light' or 'dark', never 'system'. */
export function resolveTheme(preference = getStoredTheme()) {
    if (preference === 'light' || preference === 'dark') return preference
    return systemPrefersDark() ? 'dark' : 'light'
}

/**
 * Put the choice on <html>, where the token file is watching for it.
 *
 * "system" REMOVES the attribute rather than writing the resolved value. That
 * is the difference between following the OS and taking a snapshot of it: with
 * the attribute absent, tokens.css's media query stays live, so a laptop that
 * switches to dark at sunset takes the app with it without the page being
 * reloaded or any listener being registered.
 */
export function applyTheme(preference = getStoredTheme()) {
    if (typeof document === 'undefined') return

    const root = document.documentElement

    if (preference === 'light' || preference === 'dark') {
        root.setAttribute('data-theme', preference)
    } else {
        root.removeAttribute('data-theme')
    }
}

export function setTheme(preference) {
    const next = THEMES.includes(preference) ? preference : 'system'

    try {
        localStorage.setItem(STORAGE_KEY, next)
    } catch (e) {
        // Refusing to persist should not stop the theme being applied for this
        // session.
    }

    applyTheme(next)
    return next
}

/**
 * Called once, as early as possible, before React renders.
 *
 * Applying the theme after first paint gives a flash of the wrong colours —
 * brief, but on a dark-mode machine it is a white flash in a dim room, which
 * is exactly the thing dark mode exists to avoid.
 */
export function initTheme() {
    applyTheme(getStoredTheme())
}
